import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, app } from "../../firebase";

// 6+ karakter rastgele geçici şifre üretir.
function randomSifre(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < 8; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

// Firestore members şeması (mobil + yükleme scripti bunu yazdı). TC YOK.
interface MemberDoc {
  id: string; // döküman ID = telefon (örn "05334590576")
  phone: string;
  displayName: string;
  gender: string;
  birthDate: string;
  memberSince: string;
  status: string;
  passwordChanged: boolean;
}

const MembersPage: React.FC = () => {
  const [members, setMembers] = useState<MemberDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [search, setSearch] = useState("");

  // Yeni üye ekleme formu
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ad: "", telefon: "", geciciSifre: "", gender: "", birthDate: "", status: "Aktif Üye" });
  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState("");
  const [okSifre, setOkSifre] = useState<string | null>(null); // başarı sonrası gösterilecek geçici şifre

  const openForm = () => {
    setForm({ ad: "", telefon: "", geciciSifre: randomSifre(), gender: "", birthDate: "", status: "Aktif Üye" });
    setFormError("");
    setOkSifre(null);
    setShowForm(true);
  };

  const handleAdd = async () => {
    setFormError("");
    const telDigits = form.telefon.replace(/\D/g, "");
    if (!form.ad.trim() || !telDigits) { setFormError("Ad soyad ve telefon zorunludur."); return; }
    const on = telDigits.startsWith("0") ? telDigits.slice(1) : telDigits;
    if (on.length !== 10) { setFormError("Telefon 10 haneli olmalı (0 olmadan)."); return; }
    if (form.geciciSifre.length < 6) { setFormError("Geçici şifre en az 6 karakter olmalı."); return; }

    setAdding(true);
    try {
      const functions = getFunctions(app);
      const uyeEkle = httpsCallable(functions, "uyeEkle");
      await uyeEkle({
        ad: form.ad.trim(),
        telefon: telDigits,
        geciciSifre: form.geciciSifre,
        gender: form.gender,
        birthDate: form.birthDate,
        status: form.status,
      });
      setOkSifre(form.geciciSifre); // admin'e göster (Auth'ta hash'lenir, sonra görünmez)
      await load();
    } catch (e: any) {
      const code: string = e?.code ?? "";
      if (code.includes("already-exists")) setFormError("Bu telefon numarası zaten kayıtlı.");
      else if (code.includes("permission-denied")) setFormError("Bu işlem için yetkiniz yok.");
      else if (code.includes("unauthenticated")) setFormError("Oturum gerekli, tekrar giriş yapın.");
      else if (code.includes("invalid-argument")) setFormError(e?.message || "Girdiler geçersiz.");
      else setFormError("Üye eklenemedi: " + (e?.message || "bilinmeyen hata"));
    } finally {
      setAdding(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setHata(false);
    try {
      const snap = await getDocs(query(collection(db, "members"), orderBy("displayName", "asc")));
      setMembers(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as MemberDoc[]);
    } catch (e) {
      console.error("Üyeler okunamadı:", e);
      setHata(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = members.filter((m) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (m.displayName ?? "").toLowerCase().includes(s) || (m.phone ?? "").includes(search);
  });

  return (
    <div>
      {/* Üst bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Üye Listesi</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? "Yükleniyor..." : `${members.length} üye`}
          </p>
        </div>
        <button
          onClick={openForm}
          className="flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-lg"
          style={{ background: "linear-gradient(135deg, #8B1428, #0F4C81)" }}
        >
          <i className="fa-solid fa-user-plus" /> Yeni Üye Ekle
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-gray-500 py-20 justify-center">
          <i className="fa-solid fa-circle-notch fa-spin text-2xl text-[#8B1428]" />
          Üyeler yükleniyor...
        </div>
      ) : hata ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">
          Üyeler yüklenemedi. <button onClick={load} className="underline font-semibold">Tekrar dene</button>
        </div>
      ) : members.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-users text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Henüz üye yok</p>
          <p className="text-gray-400 text-sm mt-1">Üyeler yükleme scripti ile Firestore'a eklenir</p>
        </div>
      ) : (
        <>
          {/* Arama */}
          <div className="relative mb-4">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Ad soyad veya telefon ile ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30"
            />
          </div>

          {/* Tablo */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Ad Soyad</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Telefon</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Cinsiyet</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Doğum Tarihi</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Durum</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">İlk Giriş</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-800">{m.displayName || "—"}</td>
                      <td className="px-4 py-3 text-gray-700 font-mono">{m.phone || m.id}</td>
                      <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{m.gender || "—"}</td>
                      <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{m.birthDate || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-green-50 text-green-700">
                          {m.status || "Aktif Üye"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {m.passwordChanged ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500">
                            <i className="fa-solid fa-circle-check text-green-500" /> Şifre değişti
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                            <i className="fa-solid fa-hourglass-half" /> İlk giriş bekliyor
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              {filtered.length} üye gösteriliyor {search && `("${search}" araması)`}
            </div>
          </div>
        </>
      )}

      {/* Yeni Üye Ekle Modal */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => !adding && setShowForm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative max-h-[90vh] overflow-y-auto">
              <button onClick={() => !adding && setShowForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700">
                <i className="fa-solid fa-xmark text-lg" />
              </button>

              {okSifre ? (
                // BAŞARI — geçici şifreyi admin'e göster
                <div className="text-center py-4">
                  <i className="fa-solid fa-circle-check text-green-500 text-5xl mb-4" />
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Üye Eklendi</h3>
                  <p className="text-sm text-gray-500 mb-4">Geçici şifreyi üyeye iletin. İlk girişte değiştirecektir.</p>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                    <div className="text-xs text-gray-400 uppercase font-semibold mb-1">Geçici Şifre</div>
                    <div className="text-2xl font-mono font-bold text-[#8B1428] tracking-wider select-all">{okSifre}</div>
                  </div>
                  <button onClick={() => setShowForm(false)}
                    className="text-white text-sm font-semibold px-6 py-2.5 rounded-lg"
                    style={{ background: "linear-gradient(135deg, #8B1428, #0F4C81)" }}>
                    Tamam
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-bold text-gray-800 mb-5">Yeni Üye Ekle</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Ad Soyad *</label>
                      <input type="text" value={form.ad} onChange={e => setForm({ ...form, ad: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30" placeholder="Ahmet Yılmaz" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefon * (0'sız 10 hane)</label>
                      <div className="flex items-center">
                        <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-200 rounded-l-lg text-sm text-gray-500 font-mono">0</span>
                        <input type="tel" value={form.telefon} maxLength={11}
                          onChange={e => setForm({ ...form, telefon: e.target.value })}
                          className="flex-1 border border-gray-200 rounded-r-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30" placeholder="5551234567" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Geçici Şifre * (min 6)</label>
                      <div className="flex gap-2">
                        <input type="text" value={form.geciciSifre} onChange={e => setForm({ ...form, geciciSifre: e.target.value })}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30" />
                        <button type="button" onClick={() => setForm({ ...form, geciciSifre: randomSifre() })}
                          className="px-3 py-2.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 whitespace-nowrap">
                          <i className="fa-solid fa-dice mr-1" /> Üret
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Cinsiyet</label>
                        <select value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30">
                          <option value="">—</option>
                          <option value="ERKEK">Erkek</option>
                          <option value="KADIN">Kadın</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Doğum Tarihi</label>
                        <input type="date" value={form.birthDate} onChange={e => setForm({ ...form, birthDate: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30" />
                      </div>
                    </div>

                    {formError && <div className="text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2">{formError}</div>}

                    <div className="flex gap-3 pt-2">
                      <button onClick={() => setShowForm(false)} disabled={adding}
                        className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2.5 rounded-lg hover:bg-gray-50 disabled:opacity-50">İptal</button>
                      <button onClick={handleAdd} disabled={adding}
                        className="flex-1 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg, #8B1428, #0F4C81)" }}>
                        {adding ? "Ekleniyor..." : "Ekle"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MembersPage;
